import { ArrowRight } from "lucide-react";
import { useLeadModal } from "@/components/landing/modal-context";

// Renders content immediately (no scroll-triggered animation) so the page
// feels instant — content is simply there, not fading in as you scroll.
export const Reveal = ({ children, className = "", as: Tag = "div" }) => {
  return <Tag className={className}>{children}</Tag>;
};

// Small mono eyebrow pill — the signature "zuops-style" section label.
export const Eyebrow = ({ children, testId, icon: Icon }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full bg-brand-indigo/10 px-3.5 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-brand-indigo"
  >
    {Icon ? <Icon className="h-3.5 w-3.5" /> : <span className="h-1.5 w-1.5 rounded-full bg-brand-indigo" />}
    {children}
  </span>
);

// Standard section header: eyebrow -> big Sora headline -> optional subcopy.
// `align` controls left (default) vs centered layout.
export const SectionHeader = ({
  eyebrow,
  eyebrowIcon,
  title,
  subtitle,
  align = "left",
  className = "",
  testId,
}) => {
  const centered = align === "center";
  return (
    <div
      data-testid={testId}
      className={`${centered ? "mx-auto text-center" : ""} max-w-2xl ${className}`}
    >
      {eyebrow && <Eyebrow icon={eyebrowIcon}>{eyebrow}</Eyebrow>}
      <h2 className="mt-5 font-display text-3xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-4xl lg:text-[2.85rem]">
        {title}
      </h2>
      {subtitle && (
        <p className={`mt-4 text-base leading-relaxed text-navy/55 sm:text-lg ${centered ? "mx-auto max-w-xl" : "max-w-xl"}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
};

// Primary gradient pill CTA — opens the lead modal
export const GradientCTA = ({ children, className = "", testId, showArrow = true }) => {
  const { openModal } = useLeadModal();
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={openModal}
      className={`sl-glow-cta group inline-flex items-center justify-center gap-2 rounded-full bg-brand-gradient px-6 py-3.5 font-display text-[15px] font-semibold text-white shadow-pill transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_-10px_rgba(84,87,229,0.55)] active:translate-y-0 ${className}`}
    >
      <span>{children}</span>
      {showArrow && (
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      )}
    </button>
  );
};
